import { useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { RootState } from "redux/store";
import { useGetMyBookingsQuery } from "api/account/accountApiSlice";

/**
 * #1874 — the Bookings tab (/orders) is account-aware. When accounts are
 * enabled and a profile is logged in, the order ids come from
 * GET /me/bookings instead of the device's IndexedDB (getActiveOrderIds).
 *
 * PO-decided semantic: logged in shows ONLY account-linked bookings; guest
 * tickets created on this device before login are hidden until logout.
 *
 * /me/bookings returns order UUIDs — the same ids the getUserOrder detail
 * fetch takes — wrapped as { id } to match getActiveOrderIds' shape, so
 * everything downstream of the id source (detail loading, service chips,
 * ticket cards) stays identical for both modes.
 */
export const useAccountOrderIdSource = (): {
  isAccountMode: boolean;
  accountOrderIds: Array<{ id: string }>;
} => {
  const accountsEnabled = useSelector(
    (state: RootState) => state.tenantConfig.config.accountsEnabled === true
  );
  const profile = useSelector((state: RootState) => state.account.profile);
  const isAccountMode = accountsEnabled && Boolean(profile);

  // Skipped entirely when logged out, so the guest path never hits /me.
  //
  // `refetchOnMountOrArgChange` because this query takes no argument, so every
  // visit to the tab shares one cache entry and RTK Query would otherwise
  // serve it untouched: logging in, or booking and coming back, showed the
  // previous list until a full page reload (#1873 scenario 6).
  const {
    data: accountBookings,
    refetch,
    isUninitialized,
  } = useGetMyBookingsQuery(undefined, {
    skip: !isAccountMode,
    refetchOnMountOrArgChange: true,
  });

  // ...but mounting is not the only way the tab is opened, and that is what
  // the first fix missed. `/orders` and `/orders/:orderId` render the SAME
  // component, so React never unmounts it: tapping Bookings while already on
  // a ticket — which is where the booking flow leaves you — fired no request
  // at all. Verified in the browser on integration: zero XHRs on that path.
  //
  // `location.key` changes on every navigation, including a push to the route
  // you are already on, so this covers the toolbar tab, the account page's
  // "Show my bookings" link, and the return from the booking flow alike.
  const navigationKey = useLocation().key;
  const lastNavigationKey = useRef(navigationKey);
  useEffect(() => {
    if (lastNavigationKey.current === navigationKey) return;
    lastNavigationKey.current = navigationKey;
    // On a real mount the query fetches by itself; refetching an
    // uninitialized query is an RTK error, not a no-op.
    if (isAccountMode && !isUninitialized) refetch();
  }, [navigationKey, isAccountMode, isUninitialized, refetch]);

  // Memoized: this array feeds an effect dependency in OrdersPage — a fresh
  // reference per render would re-trigger the order fetch on every render.
  const accountOrderIds = useMemo(
    () =>
      isAccountMode && accountBookings
        ? accountBookings.map((booking) => ({ id: booking.id }))
        : [],
    [isAccountMode, accountBookings]
  );

  return { isAccountMode, accountOrderIds };
};
